import { ReactNode, useMemo, useState } from 'react';
import { TextExtractor } from '../internal/TextExtractor';
import React from 'react';
import { SiteStateContext } from '../../state/SiteContext';
import { fixInternalImages, fixInternalLinks } from '../../shared/utils/path-rewrite.utils';
import { InternalStateContext } from '../../state/InternalContext';

interface CustomElementProps {
  name: string,
  children: ReactNode,
}

/**
 * A component that renders a custom element.
 * The custom element is defined by the name and the children.
 * The attributes are defined by the `arg-attribute` custom element.
 * The `arg-attribute` element must have a name attribute, which defines the attibute name.
 * The value of the attrubute is extracted from a value source component inside the 'arg-attribute' element.
 * 
 * @param name - The name of the custom element.
 * @param children - The children of the custom element.
 * @returns A React element.
 */
export function CustomElement({ name, children }: CustomElementProps): React.ReactElement {

  const [attributes, setAttributes] = useState<Record<string, string>>({});
  const [elementChildren, setElementChildren] = useState<ReactNode[]>([]);
  const siteContext = React.useContext(SiteStateContext);
  const internalContext = React.useContext(InternalStateContext);
  const sitePath = internalContext?.internalState?.isCustomDomain ? "" : (siteContext?.siteState?.sitePath || "");
  const fileApiBase = `${process.env.REACT_APP_SERVER_URL || ""}/api/app/file/${siteContext?.siteState?.site?.name || ""}`;

  type AttrElementDef = {
    node: React.ReactNode,
    name: string,
    value: string,
    callback: (value: string) => void,
  }

  const translateAttributeName = (attributeName: string): string => {
    if (attributeName === "class") {
      return "className";
    }
    if (attributeName === "for") {
      return "htmlFor";
    }
    return attributeName;
  }

  const escapeHtmlAttribute = (value: string): string => value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

  const unescapeHtmlAttribute = (value: string): string => value
    .replace(/&quot;/g, "\"")
    .replace(/&#39;/g, "'")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&");

  const attrElements: AttrElementDef[] = useMemo(() => {
    let result: AttrElementDef[] = [];
    React.Children.forEach(children, child => {
      if (React.isValidElement(child)) {
        if (child.type.toString().toLowerCase() === "arg-attribute" && child.props.name) {
          result = [...result, {
            node: child,
            name: translateAttributeName(child.props.name),
            value: "",
            callback: (value: string) => setAttributes(prev => ({ ...prev, [translateAttributeName(child.props.name)]: value })),
          }];
        }
        else if (child.type.toString().toLowerCase() === "arg-children") {
          setElementChildren(React.Children.toArray(child.props.children));
        }
      }
    });
    return result;
  }, [children]);

  const textExtractors = useMemo(() => attrElements?.map(element => {
    return <TextExtractor key={element.name} onExtractedText={element.callback}>
      {element.node}
    </TextExtractor>;
  }), [attrElements]);

  const resolvedAttributes = useMemo((): Record<string, string> => {
    const elementName = name.toLowerCase();
    return Object.entries(attributes).reduce<Record<string, string>>((resolved, [attributeName, attributeValue]) => {
      if (elementName === "a" && attributeName === "href") {
        const fixedLinkElement = fixInternalLinks(`<a href="${escapeHtmlAttribute(attributeValue)}"></a>`, sitePath);
        const hrefMatch = fixedLinkElement.match(/href=(["'])(.*?)\1/i);
        resolved[attributeName] = hrefMatch?.[2] ? unescapeHtmlAttribute(hrefMatch[2]) : attributeValue;
        return resolved;
      }

      const isMediaSrc = (elementName === "img" || elementName === "image" || elementName === "video") && attributeName === "src";
      const isVideoPoster = elementName === "video" && attributeName === "poster";
      if (isMediaSrc || isVideoPoster) {
        const fixedMediaElement = fixInternalImages(`<${elementName} ${attributeName}="${escapeHtmlAttribute(attributeValue)}" />`, fileApiBase);
        const attrMatch = fixedMediaElement.match(new RegExp(`${attributeName}=(["'])(.*?)\\1`, "i"));
        resolved[attributeName] = attrMatch?.[2] ? unescapeHtmlAttribute(attrMatch[2]) : attributeValue;
        return resolved;
      }

      resolved[attributeName] = attributeValue;
      return resolved;
    }, {});
  }, [name, attributes, sitePath, fileApiBase]);

  const customElement = useMemo(() => {
    return React.createElement(name, resolvedAttributes, elementChildren.length > 0 ? elementChildren : null);
  }, [name, resolvedAttributes, elementChildren]);

  return <>
    {customElement}
    {textExtractors}
  </>;
}